import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Helmet } from 'react-helmet-async';
import { FaArrowRight } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';

import { StyledBlogPage } from './styles';
import { LoadingIndicator } from '../../components/atoms/loading-indicator';
import { ActiveFilters } from '../../components/molecules/active-filters';
import { GradientButton } from '../../components/molecules/gradient-button';
import { NoItemsFound } from '../../components/molecules/no-items-found';
import { SearchForm } from '../../components/molecules/search-form';
import { TagsCloud } from '../../components/molecules/tags-cloud';
import { BlogCard } from '../../components/organisms/blog-card';
import { BlogCategoriesList } from '../../components/organisms/blog-categories-list';
import { useGetCategoriesQuery, useGetPopularTagsQuery, useGetPostsQuery } from '../../store/queries/posts';
import { selectBlogFilters, setBlogFilters } from '../../store/slices';
import { bemClassName } from '../../utils/bem';
import { getBlogSearchFilters } from '../../utils/get-blog-search-filters';
import { canonical, ogImage, ogUrl } from '../../utils/seo';

const b = bemClassName('blog-page');

const BlogPage = () => {
  const dispatch = useDispatch();
  const blogSearchFiltersState = useSelector(selectBlogFilters);
  const lastPageRef = useRef(0);

  const [filter, setFilter] = useState();
  const [activeFilters, setActiveFilters] = useState([]);
  const [allPosts, setAllPosts] = useState([]);

  const { data: postData, isLoading: postsLoading, isFetching: postFetching } = useGetPostsQuery(filter);
  const { data: popularTagsData } = useGetPopularTagsQuery({ limit: 11, recentPostsCount: 20 });
  const { data: categoriesData } = useGetCategoriesQuery();

  const hasMorePosts = postData && postData.currentPage < postData.totalPages;
  const hasPosts = allPosts.length > 0;
  const popularTags = popularTagsData?.map((item) => item.tag) || [];
  const categories =
    categoriesData
      ?.filter((item) => item.category) // Filter out null/undefined categories
      .map((item) => ({
        id: item.category,
        text: item.category,
        count: item.count,
      })) || [];

  const filterChangeHandler = useCallback(
    (partialState) => {
      dispatch(setBlogFilters(partialState));
    },
    [dispatch],
  );

  const handleLoadMore = useCallback(() => {
    filterChangeHandler({ page: blogSearchFiltersState.page + 1 });
  }, [blogSearchFiltersState.page, filterChangeHandler]);

  const handleSearch = useCallback(
    (search) => {
      filterChangeHandler({ search: search || null, page: 1 });
    },
    [filterChangeHandler],
  );

  const handleTagClick = useCallback(
    (tag) => {
      if (blogSearchFiltersState.tag === tag) {
        filterChangeHandler({ tag: null, page: 1 });
      } else {
        filterChangeHandler({ tag, page: 1 });
      }
    },
    [blogSearchFiltersState.tag, filterChangeHandler],
  );

  const handleCategoryClick = useCallback(
    (category) => {
      filterChangeHandler({ category: blogSearchFiltersState.category === category ? null : category, page: 1 });
    },
    [blogSearchFiltersState.category, filterChangeHandler],
  );

  const handleClearFilter = useCallback(
    (filterKey) => {
      if (filterKey === 'search') {
        filterChangeHandler({ search: null, page: 1 });
      } else if (filterKey === 'tag') {
        filterChangeHandler({ tag: null, page: 1 });
      } else if (filterKey === 'category') {
        filterChangeHandler({ category: null, page: 1 });
      }
    },
    [filterChangeHandler],
  );

  const handleClearAllFilters = useCallback(() => {
    filterChangeHandler({ search: null, tag: null, category: null, page: 1 });
  }, [filterChangeHandler]);

  // build the filter data
  useEffect(() => {
    setFilter(getBlogSearchFilters({ ...blogSearchFiltersState }));
  }, [blogSearchFiltersState]);

  // reset accumulated posts when filters (not page) change
  useEffect(() => {
    setAllPosts([]);
    lastPageRef.current = 0;
  }, [blogSearchFiltersState.search, blogSearchFiltersState.tag, blogSearchFiltersState.category]);

  // build active filter data
  useEffect(() => {
    const filterCollector = [];

    if (blogSearchFiltersState.search) {
      filterCollector.push({ key: 'search', type: 'Search', value: blogSearchFiltersState.search });
    }

    if (blogSearchFiltersState.tag) {
      filterCollector.push({ key: 'tag', type: 'Tag', value: blogSearchFiltersState.tag });
    }

    if (blogSearchFiltersState.category) {
      filterCollector.push({ key: 'category', type: 'Category', value: blogSearchFiltersState.category });
    }

    setActiveFilters(filterCollector);
  }, [blogSearchFiltersState]);

  // Refresh posts data on filter change
  useEffect(() => {
    if (postsLoading || postFetching || !postData?.data) return;

    if (postData?.currentPage !== lastPageRef.current) {
      lastPageRef.current = postData?.currentPage;

      setAllPosts((prev) => {
        const existingIds = new Set(prev.map((p) => p._id));
        const newPosts = postData.data.filter((p) => !existingIds.has(p._id));

        const allData = postData?.currentPage === 1 ? newPosts : [...prev, ...newPosts];

        return allData;
      });
    }
  }, [postData?.currentPage, postData?.data, postFetching, postsLoading]);

  return (
    <StyledBlogPage>
      <Helmet>
        <title>Health & Fitness Blog — Tips & Insights | Active Shape</title>
        <meta
          name="description"
          content="Explore nutrition tips, fitness guides, healthy recipes, and wellness insights from the Active Shape team to help you reach your health goals."
        />
        {canonical('/blog') && <link rel="canonical" href={canonical('/blog')} />}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Health & Fitness Blog — Tips & Insights | Active Shape" />
        <meta
          property="og:description"
          content="Explore nutrition tips, fitness guides, healthy recipes, and wellness insights from the Active Shape team to help you reach your health goals."
        />
        {ogImage() && <meta property="og:image" content={ogImage()} />}
        {ogUrl('/blog') && <meta property="og:url" content={ogUrl('/blog')} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Health & Fitness Blog — Tips & Insights | Active Shape" />
        <meta
          name="twitter:description"
          content="Explore nutrition tips, fitness guides, healthy recipes, and wellness insights from the Active Shape team to help you reach your health goals."
        />
        {ogImage() && <meta name="twitter:image" content={ogImage()} />}
      </Helmet>
      <div className={b()}>
        <div className={b('left')}>
          <ActiveFilters filters={activeFilters} onClearFilter={handleClearFilter} onClearAll={handleClearAllFilters} />

          {postsLoading && <LoadingIndicator size={16} />}
          {!postsLoading &&
            hasPosts &&
            allPosts.map((post) => (
              <div key={`blog-card-item-${post._id}`} className={b('left', 'item')}>
                <BlogCard
                  image={post.coverImageUrl}
                  tags={post.tags}
                  text={post.excerpt}
                  icon={<FaArrowRight />}
                  iconText={'Read More'}
                  iconTextLink={`/blog/${post.slug}`}
                  iconAriaLabel={`Read more: ${post.title}`}
                />
              </div>
            ))}

          {!postFetching && !hasPosts && (
            <div className={b('left', 'no-items-found')}>
              <NoItemsFound />
            </div>
          )}

          {hasMorePosts && (
            <div className={b('left', 'load-more')}>
              <GradientButton onClick={handleLoadMore} loading={postFetching} disabled={postFetching}>
                Load More Posts
              </GradientButton>
            </div>
          )}
        </div>

        <div className={b('right')}>
          <SearchForm onSearch={handleSearch} />
          <BlogCategoriesList title={'Categories'} categories={categories} onCategoryClick={handleCategoryClick} />
          <TagsCloud title={'Popular Tags'} tags={popularTags} onTagClick={handleTagClick} clickable />
        </div>
      </div>
    </StyledBlogPage>
  );
};

export default BlogPage;
